/**
 * =============================================================================
 *
 * @format
 */

/*===============================  IMPORTS  ===================================*/
/*=============================================================================*/
import React, { useEffect, useState } from "react";
import Customize_Reworked from "./index";
import { useDispatch, useSelector } from "react-redux";
import { description, title } from "../../constants/tooltipConstants";
import {
	customizeProduct,
	getDrivePrice,
	getValances,
} from "../../actions/blindActions";
import axios from "../../lib/axios";
import { AnimatePresence } from "framer-motion";
import ImageZoomWithButton from "../../components/button/ImageZoomWithButton";
import { motion } from "framer-motion";
import { Play, Settings, X } from "lucide-react";
import { Toast } from "./Toast";
import { Tooltip } from "./Tooltip";

const Customize_Control = () => {
	/*=============================================================================*/
	/*===============================  VARIABLES  =================================*/
	/*=============================================================================*/
	const [loading, setLoading] = useState(true);
	const dispatch = useDispatch();
	const { productInfo, optionInfo } = useSelector(
		(state) => state.customize.customizeProduct
	);
	const [charger, setCharger] = useState(productInfo.charger);
	const [connector, setConnector] = useState(productInfo.connector);
	const [remote, setRemote] = useState(productInfo.remote);
	const [errorToast, setErrorToast] = useState(false);
	const [videoSrc, setVideoSrc] = useState(false);
	const [videoOpen, setVideoOpen] = useState(false);
	const [position, setPosition] = useState(productInfo.position);
	const [system, setSystem] = useState(productInfo.Operation);
	const [tube, setTube] = useState(productInfo.tube);
	const [data, setData] = useState(null);
	const shadeWidthFrac = optionInfo.widthFraction;
	const shadeLengthFrac = optionInfo.lengthFraction;
	const shadeLength = optionInfo.lengthWhole;
	const shadeWidth = optionInfo.widthWhole;
	const shadeTitle = productInfo.Type.toLowerCase() + " shade";
	const fabricType =
		productInfo.Fabric_Code.includes("Gossamer-") &&
		!productInfo.Fabric_Code.includes("Pale")
			? productInfo.Fabric_Code.replace(/\s/g, "")
			: productInfo.Fabric_Code;
	const hemType = productInfo.Bottom_Bar;
	const country = useSelector((state) => state.country);
	const [systems, setSystems] = useState([
		"Chain",
		"Motor",
		"Zero Gravity",
		"Chainless",
	]);

	/*=============================================================================*/
	/*===============================  USE EFFECT  ================================*/
	/*=============================================================================*/
	useEffect(() => {
		loadDrive();
	}, []);

	useEffect(() => {
		dispatchDetails();
	}, [remote, charger, connector, position, system]);

	/*=============================================================================*/
	/*===============================  FUNCTIONS  =================================*/
	/*=============================================================================*/
	const displayCountryPrice = (price) => {
		price = parseFloat(price);
		const discount1 = parseFloat(productInfo?.Discount) || 0;

		// Extra discount sets here, if you want to enable it, set extraDiscount > 0
		// For this event, we set a global 15% AFTER the first discount applies
		const discount2 = parseFloat(productInfo?.Global_Discount) || 0; // Have to set to 0.15 for default, otherwise customized product (does not belong to any "program") will not have discount

		if (country == "USA") {
			return `$${(
				price *
				1.5 *
				(1 - discount1) *
				(1 - discount2) *
				0.75
			).toFixed(2)} USD`;
		} else {
			return `$${(price * 1.5 * (1 - discount1) * (1 - discount2)).toFixed(
				2
			)} CAD`;
		}
	};

	const playVideo = (item) => {
		switch (item) {
			case "Chain":
				setVideoSrc("/videos/SS_animation.mp4");
				break;
			case "Motor":
				setVideoSrc("/videos/Remote_animation.mp4");
				break;
			case "Zero Gravity":
				setVideoSrc("/videos/ZG_animation.mp4");
				break;
			case "Chainless":
				setVideoSrc("/videos/Chainless_animation.mp4");
				break;
		}
		setVideoOpen(true);
	};

	const getCompatibleSystems = async (totalWidth, totalLength) => {
		// Map over the array asynchronously and await the promises
		const filteredData = await Promise.all(
			systems.map(async (operation) => {
				if (
					!hemType.includes("Accubar") &&
					!hemType.includes("Interlude 2.0") &&
					operation === "Zero Gravity"
				) {
					return undefined;
				}

				const jsonData = JSON.stringify({
					width: totalWidth,
					length: totalLength,
					fabric: fabricType,
					bottom_bar: hemType,
					operation: operation,
					motor: operation === "Motor" ? "Stealth Rechargeable Motor" : "None",
					shade: shadeTitle,
					lam_needed: "false",
					company: "Sun Boss",
				});

				const res = await axios.post(
					"https://backend.mysunglow.com/get_tube",
					jsonData
				);

				const data = res.data;

				console.log("json data for operation", operation, jsonData, data);
				if (data?.status !== "Not compatible") {
					console.log("Operation: ", operation);
					return operation;
				} else {
					return undefined;
				}
			})
		);
		// Filter out any undefined values (if any)
		const filteredResult = filteredData.filter(
			(operation) => operation !== undefined
		);

		return filteredResult;
	};

	const getTubes = async (totalWidth, totalLength) => {
		const jsonObj = {
			width: totalWidth,
			length: totalLength,
			fabric: fabricType,
			bottom_bar: hemType,
			operation: system,
			motor: system === "Motor" ? "Stealth Rechargeable Motor" : "None",
			shade: shadeTitle,
			lam_needed: "false",
			company: "Sun Boss",
		};

		const res = await axios.post(
			"https://backend.mysunglow.com/get_tube",
			jsonObj
		);

		try {
			const valances = await getValances(
				res.data.info?.drive?.Drive,
				res.data.info?.tube?.max_rud,
				shadeTitle.replace(" shade", ""),
				totalWidth
			);

			console.log("Valances", valances);

			setTube(res.data.info.tube.Description);

			dispatch(
				customizeProduct({
					productInfo: {
						...productInfo,
						remote: remote,
						charger: charger,
						connector: connector,
						position: position,
						Operation: system,
						spring: res.data.info.spring,
						tube: res.data.info.tube.Description,
						torque: res.data.info.tube.needed_torque,
						valanceArray: valances.valanceResult,
					},
					optionInfo: optionInfo,
				})
			);
		} catch (error) {
			console.log("Error getting valances", error);
		}
	};

	const loadDrive = async () => {
		let totalWidth = shadeWidthFrac
			? parseFloat(shadeWidth) + eval(shadeWidthFrac)
			: shadeWidth;
		let totalLength = shadeLengthFrac
			? parseFloat(shadeLength) + eval(shadeLengthFrac)
			: shadeLength;

		if (totalLength % 1 !== 0) {
			totalLength = eval(totalLength);
		}
		if (totalWidth % 1 !== 0) {
			totalWidth = eval(totalWidth);
		}

		await getTubes(totalWidth, totalLength);

		const drivePrices = await getDrivePrice(totalWidth, totalLength);
		setData(drivePrices);

		const systems = await getCompatibleSystems(totalWidth, totalLength);
		if (systems.length === 0) {
			alert(
				"No compatible systems found for this shade. Please contact customer service."
			);
			return;
		}

		console.log("Systems", systems);
		setSystems(systems);
		setLoading(false);

		// if (!res.system.includes(system)) {
		// 	setSystem("Chain");
		// }
	};

	const dispatchDetails = () => {
		dispatch(
			customizeProduct({
				productInfo: {
					...productInfo,
					remote: remote,
					charger: charger,
					connector: connector,
					position: position,
					Operation: system,
					tube: tube,
				},
				optionInfo: optionInfo,
			})
		);
	};

	useEffect(() => {
		let totalWidth = shadeWidthFrac
			? parseFloat(shadeWidth) + eval(shadeWidthFrac)
			: shadeWidth;
		let totalLength = shadeLengthFrac
			? parseFloat(shadeLength) + eval(shadeLengthFrac)
			: shadeLength;

		if (totalLength % 1 !== 0) {
			totalLength = eval(totalLength);
		}
		if (totalWidth % 1 !== 0) {
			totalWidth = eval(totalWidth);
		}

		getTubes(totalWidth, totalLength);
	}, [system]);

	function getDriveTypeDisplayName(item) {
		if (item === "Zero Gravity") {
			return "Chainless Manual";
		} else if (item === "Chain") {
			return "Chain Manual";
		} else if (item === "Motor") {
			return "Automated";
		} else {
			return item;
		}
	}
	/*=============================================================================*/
	/*===============================  RENDER  ====================================*/
	/*=============================================================================*/
	return (
		<Customize_Reworked>
			<div className="space-y-8">
				{/* Header */}
				<div className="flex items-center gap-3">
					<div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
						<Settings className="w-6 h-6 text-primary" />
					</div>
					<h3 className="text-lg font-medium">Select Control System</h3>
				</div>

				{loading ? (
					<div className="flex items-center justify-center h-32">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
					</div>
				) : (
					<>
						{/* Video Modal */}
						{videoOpen && (
							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center"
								onClick={() => setVideoOpen(false)}
							>
								<motion.video
									initial={{ scale: 0.5 }}
									animate={{ scale: 1 }}
									exit={{ scale: 0.5 }}
									src={videoSrc}
									controls
									autoPlay
									muted
									className="max-w-[90%] max-h-[90%]"
								/>
								<button
									onClick={() => setVideoOpen(false)}
									className="absolute top-4 right-4 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg"
								>
									<X className="w-5 h-5 text-primary" />
								</button>
							</motion.div>
						)}

						{/* Control Systems */}
						<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
							{systems.map((item) => (
								<motion.div
									key={item}
									whileHover={{ scale: 1.02 }}
									whileTap={{ scale: 0.98 }}
									className={`
						relative p-4 border-2 rounded-2xl cursor-pointer transition-all
						${
							system.toLowerCase() === item.toLowerCase()
								? "border-primary bg-primary/5 shadow-lg shadow-primary/10"
								: "border-gray-200 hover:border-primary/30"
						}
					  `}
									onClick={() => setSystem(item)}
								>
									{/* Image */}
									<div className="rounded-lg mb-4">
										<ImageZoomWithButton
											item={{
												image: `/images/customizeToolRenders/${item}.png`,
												colour: getDriveTypeDisplayName(item),
											}}
											isFabricSelected={
												system.toLowerCase() === item.toLowerCase()
											}
											customize={true}
										/>
									</div>

									{/* Video icon */}

									<button
										onClick={() => playVideo(item)}
										className="absolute top-2 left-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg"
									>
										<Play className="w-5 h-5 text-primary" />
									</button>

									{/* Content */}
									<div className="space-y-4">
										{/* Title & Info */}
										<div className="flex items-center justify-between">
											<div className="flex items-center gap-2">
												<input
													type="radio"
													name="control"
													id={item}
													checked={system.toLowerCase() === item.toLowerCase()}
													onChange={() => setSystem(item)}
													className="w-4 h-4 text-primary"
												/>
												<label htmlFor={item} className="font-medium">
													{getDriveTypeDisplayName(item)}
												</label>
												<Tooltip
													title={title[item]}
													content={description[item]}
												>
													<button className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-100">
														?
													</button>
												</Tooltip>
											</div>
										</div>

										{/* Price */}
										<p className="text-primary font-medium">
											{item !== "Chain"
												? `+${displayCountryPrice(data.driveInfo[item])}`
												: "Included"}
										</p>

										{/* Additional Options for Motor */}
										{item === "Motor" && system === "Motor" && (
											<div className="space-y-4 pt-4 border-t">
												{[
													{
														name: "Remote",
														state: remote,
														setState: setRemote,
														tooltip:
															"At least 1 remote is required to operate the shade. One remote can operate up to 15 shades.",
														price: data.addonInfo["remote"],
													},
													{
														name: "Charger",
														state: charger,
														setState: setCharger,
														tooltip:
															"5V USB type C Cable and USB Wall Plug charger (output 5V 2A) 9.8ft, White.",
														price: data.addonInfo["charger"],
													},
													{
														name: "Connector",
														state: connector,
														setState: setConnector,
														tooltip: "Connects multiple shades together",
														price: data.addonInfo["connector"],
													},
												].map((addon) => (
													<div key={addon.name} className="space-y-2">
														<div className="flex items-center justify-between">
															<div className="flex items-center gap-2">
																<span className="font-medium">
																	{addon.name}
																</span>
																<Tooltip
																	title={addon.name}
																	content={addon.tooltip}
																>
																	<button className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-100">
																		?
																	</button>
																</Tooltip>
															</div>
															<p className="text-primary font-medium">
																+{displayCountryPrice(addon.price)}
															</p>
														</div>
														<select
															value={addon.state}
															onChange={(e) => addon.setState(e.target.value)}
															className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
														>
															<option value="No">No</option>
															<option value="Yes">Yes</option>
														</select>
													</div>
												))}
											</div>
										)}
									</div>
								</motion.div>
							))}
						</div>

						{/* Position Selection */}
						{system !== "Zero Gravity" && (
							<div className="space-y-4">
								<h4 className="font-medium">Control Position</h4>
								<div className="flex gap-4">
									{["Left", "Right"].map((pos) => (
										<motion.div
											key={pos}
											whileHover={{ scale: 1.02 }}
											whileTap={{ scale: 0.98 }}
											className={`
							max-w-[200px] relative p-4 border-2 rounded-2xl cursor-pointer transition-all
							${
								position === pos
									? "border-primary bg-primary/5 shadow-lg shadow-primary/10"
									: "border-gray-200 hover:border-primary/30"
							}
						  `}
											onClick={() => setPosition(pos)}
										>
											{/* Image */}
											<div className="rounded-lg mb-4">
												<ImageZoomWithButton
													item={{
														image: `/images/customizeToolRenders/${pos}.png`,
														colour: `Position ${pos}`,
													}}
													isFabricSelected={position === pos}
													customize={true}
												/>
											</div>

											{/* Content */}
											<div className="flex items-center justify-between">
												<div className="flex items-center gap-2">
													<input
														type="radio"
														name="position"
														id={`pos-${pos}`}
														checked={position === pos}
														onChange={() => setPosition(pos)}
														className="w-4 h-4 text-primary"
													/>
													<label htmlFor={`pos-${pos}`} className="font-medium">
														Position {pos}
													</label>
													<Tooltip
														title={`Position ${pos}`}
														content="Place the control system on the side that offers you more convenience and accessibility."
													>
														<button className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-100">
															?
														</button>
													</Tooltip>
												</div>
											</div>
										</motion.div>
									))}
								</div>
							</div>
						)}
					</>
				)}

				{/* Error Toast */}
				<AnimatePresence>
					<Toast
						show={errorToast}
						message="Please select a chain color."
						onClose={() => setErrorToast(false)}
					/>
				</AnimatePresence>
			</div>
		</Customize_Reworked>
	);
};

export default Customize_Control;
